import React, { useEffect } from "react";
import { useParams } from "react-router";
import Lights from "./Lights";
import Timer from "./Timer";
import LoadingChart from "./LoadingChart";
import NextLoad from "./NextLoad";
import OrderTable from "./OrderTable";
import PositionChange from "./PositionChange";
import "./DisplayIndex.scss";
import { useMeet } from "util/useMeet";
import { usePlatform } from "util/usePlatform";
import { useCurrentLifter } from "util/useCurrentLifter";
import { useCurrentAttempt } from "util/useCurrentAttempt";
import { useKnownLiftingOrder } from "util/useKnownLiftingOrder";
import { useCompleteLiftingOrder } from "util/useCompleteLiftingOrder";
import { useFedConfig } from "util/useFedConfig";
import LifterName from "./LifterName";
import AttemptInfo from "./AttemptInfo";
import TeamName from "./TeamName";
import DivisionInfo from "./DivisionInfo";
import RecordInfo from "./RecordInfo";
import classNames from "classnames";
import {
  DisplaySettingsProvider,
  DisplayState,
} from "./DisplaySettingsProvider";
import { DisplaySettingsPanel } from "./DisplaySettingsPanel/DisplaySettingsPanel";
import NextOverlay from "./NextOverlay";
import CurrentOverlay from "./CurrentOverlay";
import Marquee from "./Marquee";
import Image from "./Image";
import { getBarAndCollarsWeight } from "util/barLoad";
import Standings from "./Standings";
import Text from "./Text";
import { useRemoteScreen } from "util/useRemoteScreen";
import { useScoreBoardTableData } from "util/useScoreBoardTableData";
import GearIcon from "icons/GearIcon";
import Scoreboard from "./Scoreboard";

const DisplayIndex = ({ stateOverride }: { stateOverride?: DisplayState }) => {
  const { platformId, remoteScreenId } = useParams<{
    platformId: string;
    remoteScreenId: string;
  }>();
  const isRemoteViewer = Boolean(remoteScreenId);
  const meet = useMeet();
  const remoteScreen = useRemoteScreen();

  const [remoteMode, setRemoteMode] = React.useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = React.useState(false);
  const [selectedRemoteScreenIdState, setSelectedRemoteScreenId] =
    React.useState<string | null>(null);

  const selectedRemoteScreenId = remoteMode
    ? selectedRemoteScreenIdState
    : null;

  const activePlatformId = isRemoteViewer
    ? remoteScreen?.platformId
    : platformId;

  const platform = usePlatform(activePlatformId);
  const currentLifter = useCurrentLifter(activePlatformId);
  const currentAttempt = useCurrentAttempt(activePlatformId);
  const knownLiftingOrder = useKnownLiftingOrder(activePlatformId);
  const completeLiftingOrder = useCompleteLiftingOrder(activePlatformId);
  const scoreBoardRows = useScoreBoardTableData(activePlatformId);
  const federationConfig = useFedConfig();

  const [barChangeEvent, setBarChangeEvent] = React.useState(false);

  const remoteScreenUrl =
    selectedRemoteScreenId && meet._id
      ? `${window.location.origin}/meets/${meet._id}/remote/${selectedRemoteScreenId}`
      : "";

  const barAndCollarsWeight = getBarAndCollarsWeight({
    attempt: currentAttempt,
    lifter: currentLifter,
    platform,
  });
  const barAndCollarsWeightRef = React.useRef(barAndCollarsWeight);
  const platformIdRef = React.useRef(platform?._id);
  const barChangeEventTimer = React.useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    if (!platform?._id) {
      return;
    }

    if (
      platformIdRef.current === platform._id &&
      Number(barAndCollarsWeight) !== Number(barAndCollarsWeightRef.current)
    ) {
      setBarChangeEvent(true);

      barChangeEventTimer.current = setTimeout(() => {
        setBarChangeEvent(false);
      }, 5000);
    }

    barAndCollarsWeightRef.current = barAndCollarsWeight;

    return () => {
      if (barChangeEventTimer.current) {
        clearTimeout(barChangeEventTimer.current);
      }
    };
  }, [platform?._id, barAndCollarsWeight]);

  useEffect(() => {
    if (
      !isRemoteViewer &&
      !remoteMode &&
      meet.displayDefaultURL &&
      !window.location.search
    ) {
      window.location.search = meet.displayDefaultURL;
    }
  }, [isRemoteViewer, meet.displayDefaultURL, remoteMode]);

  if (!meet || !platform) {
    return null;
  }

  return (
    <DisplaySettingsProvider
      stateOverride={stateOverride}
      isRemoteViewer={isRemoteViewer}
      remoteScreenId={remoteScreenId ?? selectedRemoteScreenId}
      editMode={settingsPanelOpen}
    >
      <div
        className={classNames(
          "display-index2",
          remoteMode && !isRemoteViewer && "remote-mode",
          isRemoteViewer && "remote-viewer",
        )}
      >
        {!isRemoteViewer && (
          <div className="display-settings-button">
            <button
              aria-label="Open display settings"
              title="Open display settings"
              onClick={() => setSettingsPanelOpen((prev) => !prev)}
            >
              <GearIcon />
            </button>
          </div>
        )}
        {settingsPanelOpen && (
          <DisplaySettingsPanel
            onCloseSettings={() => setSettingsPanelOpen(false)}
            remoteMode={remoteMode}
            setRemoteMode={setRemoteMode}
            selectedRemoteScreenId={selectedRemoteScreenId}
            setSelectedRemoteScreenId={setSelectedRemoteScreenId}
            remoteScreenUrl={remoteScreenUrl}
          ></DisplaySettingsPanel>
        )}
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <Image id="i" meet={meet} />
          <LifterName currentLifter={currentLifter} id="b" />
          <AttemptInfo meet={meet} currentAttempt={currentAttempt} id="a" />
          <TeamName meet={meet} currentLifter={currentLifter} id="t" />
          <DivisionInfo meet={meet} currentLifter={currentLifter} id="d" />
          <RecordInfo currentAttempt={currentAttempt} id="r" />
          <Timer platform={platform} id="time" />
          <PositionChange
            meet={meet}
            currentAttempt={currentAttempt}
            currentLifter={currentLifter}
            id="p"
          />
          <Lights meet={meet} platformId={platform._id} id="l" />
          <LoadingChart
            meet={meet}
            currentLifter={currentLifter}
            currentAttempt={currentAttempt}
            platform={platform}
            barChangeEvent={barChangeEvent}
            id="c"
          />
          <OrderTable
            meet={meet}
            platform={platform}
            completeLiftingOrder={completeLiftingOrder}
            id="o"
            federationConfig={federationConfig}
          />
          <Scoreboard
            meet={meet}
            currentAttempt={currentAttempt}
            currentLifter={currentLifter}
            rows={scoreBoardRows}
            id="sb"
            federationConfig={federationConfig}
          />
          <NextLoad
            meet={meet}
            currentAttempt={currentAttempt}
            currentLifter={currentLifter}
            liftingOrder={knownLiftingOrder}
            platform={platform}
            id="n"
          />
          <NextOverlay id="no" platformId={platform._id} />
          <CurrentOverlay id="co" platformId={platform._id} />
          <Marquee
            meet={meet}
            currentLifter={currentLifter}
            currentAttempt={currentAttempt}
            id="m"
            platform={platform}
            barChangeEvent={barChangeEvent}
          />
          <Standings id="s" />
          <Text id="tx" />
        </div>
      </div>
    </DisplaySettingsProvider>
  );
};

export default DisplayIndex;
